import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import * as yup from 'yup';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import type { Tuple } from '@clearverify/types';

import { Button } from 'src/components/common';
import { Field, Form, SelectField } from 'src/components/finalForm';
import type { SelectFieldOption } from 'src/types/components';

export const SelectInputSection: React.FC = () => {
  return (
    <Box component="section">
      <Typography variant="h3">Select Input</Typography>
      <Box mt={2}>
        <Form
          initialValues={initialValue}
          onSubmit={submitHandler}
          subscription={{ dirty: true }}
          validate={validateWithYupSchema(formValidationSchema)}
        >
          {({ form, dirty }) => (
            <React.Fragment>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.emptySelect}
                    component={SelectField}
                    fullWidth
                    label="Empty options"
                    options={[]}
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.emptySelectDisabled}
                    component={SelectField}
                    fullWidth
                    label="Empty options"
                    options={[]}
                    disabled
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithOptions}
                    component={SelectField}
                    fullWidth
                    label="With options"
                    options={options}
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithOptionsDisabled}
                    component={SelectField}
                    fullWidth
                    label="With options"
                    options={options}
                    disabled
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithOptionsRequired}
                    component={SelectField}
                    fullWidth
                    label="With options (required)"
                    options={options}
                    required
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithOptionsRequiredDisabled}
                    component={SelectField}
                    fullWidth
                    label="With options (required)"
                    options={options}
                    required
                    disabled
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithSelectedOption}
                    component={SelectField}
                    fullWidth
                    label="With selected option"
                    options={options}
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithSelectedOptionDisabled}
                    component={SelectField}
                    fullWidth
                    label="With selected option"
                    options={options}
                    disabled
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithSelectedOptionInvalid}
                    component={SelectField}
                    fullWidth
                    label="With selected option (required, initially invalid)"
                    options={options}
                    required
                    showErrorOnlyTouched={false}
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    name={FormFieldId.selectWithSelectedOptionInvalidDisabled}
                    component={SelectField}
                    fullWidth
                    label="With selected option (required, initially invalid)"
                    options={options}
                    required
                    showErrorOnlyTouched={false}
                    disabled
                  />
                </Grid>
              </Grid>

              <Box mt={2}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => form.restart()}
                  disabled={!dirty}
                >
                  Reset
                </Button>
              </Box>
            </React.Fragment>
          )}
        </Form>
      </Box>
    </Box>
  );
};

const options: Tuple<SelectFieldOption, 3> = [
  {
    value: '1',
    label: 'Menu Item 1',
  },
  {
    value: '2',
    label: 'Menu Item 2',
  },
  {
    value: '3',
    label: 'Menu Item 2',
  },
];

enum FormFieldId {
  emptySelect = 'emptySelect',
  emptySelectDisabled = 'emptySelectDisabled',
  selectWithOptions = 'selectWithOptions',
  selectWithOptionsDisabled = 'selectWithOptionsDisabled',
  selectWithOptionsRequired = 'selectWithOptionsRequired',
  selectWithOptionsRequiredDisabled = 'selectWithOptionsRequiredDisabled',
  selectWithSelectedOption = 'selectWithSelectedOption',
  selectWithSelectedOptionDisabled = 'selectWithSelectedOptionDisabled',
  selectWithSelectedOptionInvalid = 'selectWithSelectedOptionInvalid',
  selectWithSelectedOptionInvalidDisabled = 'selectWithSelectedOptionInvalidDisabled',
}

const initialValue = {
  [FormFieldId.emptySelect]: undefined,
  [FormFieldId.emptySelectDisabled]: undefined,
  [FormFieldId.selectWithOptions]: undefined,
  [FormFieldId.selectWithOptionsDisabled]: undefined,
  [FormFieldId.selectWithSelectedOption]: options[0].value,
  [FormFieldId.selectWithSelectedOptionDisabled]: options[0].value,
  [FormFieldId.selectWithSelectedOptionInvalid]: options[0].value,
  [FormFieldId.selectWithSelectedOptionInvalidDisabled]: options[0].value,
};

const submitHandler = () => undefined;

const selectInputValidationSchema = yup
  .string()
  .required('Required')
  .test('test', 'Select another option', value => value !== options[0].value);

const formValidationSchema = yup.object({
  [FormFieldId.selectWithOptionsRequired]: selectInputValidationSchema,
  [FormFieldId.selectWithOptionsRequiredDisabled]: selectInputValidationSchema,
  [FormFieldId.selectWithSelectedOptionInvalid]: selectInputValidationSchema,
  [FormFieldId.selectWithSelectedOptionInvalidDisabled]: selectInputValidationSchema,
});
